import React, { useState, useEffect } from 'react';
import { 
  FaKey, 
  FaLock, 
  FaEye, 
  FaEyeSlash, 
  FaSave, 
  FaCheck, 
  FaTimes,
  FaEdit,
  FaGoogle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaShieldAlt
} from 'react-icons/fa';
import { RefreshCw } from 'lucide-react';
import { db } from '../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import styles from '../CSS/APIWorkspace.module.css';

const APIWorkspace = () => {
  const [config, setConfig] = useState({
    googleApiKey: '',
    googleClientId: ''
  });

  const [showKeys, setShowKeys] = useState({ apiKey: false, clientId: false });
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [errors, setErrors] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(true);
  
  // Estados para manejo de claves guardadas
  const [savedKeys, setSavedKeys] = useState({ apiKey: false, clientId: false });
  const [editingKeys, setEditingKeys] = useState({ apiKey: false, clientId: false });
  const [displayKeys, setDisplayKeys] = useState({ apiKey: '', clientId: '' });
  
  // Constante para placeholder de claves guardadas
  const SAVED_KEY_PLACEHOLDER = '••••••••••••••••••••••••••••••••••••••••';
  
  // Contraseña fija para encriptación (solo para almacenamiento, no para UI)
  const ENCRYPTION_KEY = 'admin_encrypt_2024';

  // Función simple de encriptación
  const simpleEncrypt = (text) => {
    if (!text) return '';
    return btoa(text + '|' + ENCRYPTION_KEY);
  };

  const simpleDecrypt = (encryptedText) => {
    if (!encryptedText) return '';
    try {
      const decoded = atob(encryptedText);
      const [text, storedKey] = decoded.split('|');
      return storedKey === ENCRYPTION_KEY ? text : '';
    } catch {
      return '';
    }
  };

  // Cargar configuración desde Firebase
  useEffect(() => {
    const loadConfig = async () => {
      setLoadingConfig(true);
      try {
        const docRef = doc(db, 'settings', 'google_api');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          
          // Verificar si hay claves guardadas
          const hasApiKey = data.googleApiKey && data.googleApiKey.trim() !== '';
          const hasClientId = data.googleClientId && data.googleClientId.trim() !== '';
          
          setSavedKeys({
            apiKey: hasApiKey,
            clientId: hasClientId
          });
          
          // Configurar display keys
          setDisplayKeys({
            apiKey: hasApiKey ? SAVED_KEY_PLACEHOLDER : '',
            clientId: hasClientId ? SAVED_KEY_PLACEHOLDER : ''
          });
          
          setEditingKeys({
            apiKey: !hasApiKey,
            clientId: !hasClientId
          });
          
          // Guardar versiones encriptadas en el estado
          setConfig({
            googleApiKey: data.googleApiKey || '',
            googleClientId: data.googleClientId || ''
          });
          
          console.log('Configuración API Workspace cargada desde Firebase');
        } else {
          console.log('No existe configuración API Workspace previa');
          setSavedKeys({ apiKey: false, clientId: false });
          setEditingKeys({ apiKey: true, clientId: true });
        }
      } catch (error) {
        console.error('Error loading API Workspace config:', error);
        setTestResult({
          success: false,
          message: 'Error al cargar la configuración'
        });
      } finally {
        setLoadingConfig(false);
      }
    };
    
    loadConfig();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    
    // Validar Google API Key
    if (!savedKeys.apiKey || editingKeys.apiKey) {
      if (!config.googleApiKey.trim()) {
        newErrors.googleApiKey = 'La Google API Key es requerida';
      } else if (config.googleApiKey.length < 30) {
        newErrors.googleApiKey = 'La API Key parece ser muy corta';
      }
    }
    
    // Validar Google Client ID
    if (!savedKeys.clientId || editingKeys.clientId) {
      if (!config.googleClientId.trim()) {
        newErrors.googleClientId = 'El Google Client ID es requerido';
      } else if (!config.googleClientId.includes('.googleusercontent.com')) {
        newErrors.googleClientId = 'El Client ID debe contener .googleusercontent.com';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
    
    setDisplayKeys(prev => ({
      ...prev,
      [field === 'googleApiKey' ? 'apiKey' : 'clientId']: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
    
    setIsSaved(false);
  };

  const handleEditKey = (keyType) => {
    setEditingKeys(prev => ({
      ...prev,
      [keyType]: true
    }));
    
    // ✅ IGUAL QUE SMTP: Resetear el campo por privacidad cuando se edita
    setDisplayKeys(prev => ({
      ...prev,
      [keyType]: ''
    }));
    
    setConfig(prev => ({
      ...prev,
      [keyType === 'apiKey' ? 'googleApiKey' : 'googleClientId']: ''
    }));
  };

  const handleCancelEdit = (keyType) => {
    if (savedKeys[keyType]) {
      setEditingKeys(prev => ({
        ...prev,
        [keyType]: false
      }));
      
      setDisplayKeys(prev => ({
        ...prev,
        [keyType]: SAVED_KEY_PLACEHOLDER
      }));
      
      // ✅ RESTAURAR la clave encriptada original en el config
      // No necesitamos desencriptarla, mantenemos la versión encriptada
      const field = keyType === 'apiKey' ? 'googleApiKey' : 'googleClientId';
      // El config ya tiene la versión encriptada correcta, no necesitamos cambiarla
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      // ✅ LÓGICA MEJORADA: Solo encriptar las claves que se están editando
      let finalApiKey = config.googleApiKey;
      let finalClientId = config.googleClientId;
      
      // Si estamos editando la API Key, encriptarla
      if (editingKeys.apiKey || !savedKeys.apiKey) {
        finalApiKey = simpleEncrypt(config.googleApiKey);
      }
      // Si no se está editando pero ya existía, mantener la versión encriptada actual
      
      // Si estamos editando el Client ID, encriptarlo
      if (editingKeys.clientId || !savedKeys.clientId) {
        finalClientId = simpleEncrypt(config.googleClientId);
      }
      // Si no se está editando pero ya existía, mantener la versión encriptada actual
      
      const docRef = doc(db, 'settings', 'google_api');
      await setDoc(docRef, {
        googleApiKey: finalApiKey,
        googleClientId: finalClientId,
        updatedAt: new Date().toISOString(),
        updatedBy: 'admin'
      });
      
      // Actualizar estados después de guardar
      setIsSaved(true);
      setSavedKeys({ apiKey: true, clientId: true });
      setEditingKeys({ apiKey: false, clientId: false });
      setDisplayKeys({
        apiKey: SAVED_KEY_PLACEHOLDER,
        clientId: SAVED_KEY_PLACEHOLDER
      });
      
      // Actualizar config con versiones encriptadas finales
      setConfig({
        googleApiKey: finalApiKey,
        googleClientId: finalClientId
      });
      
      setTestResult({
        success: true,
        message: 'Configuración de API guardada correctamente y encriptada'
      });
      
      setTimeout(() => {
        setIsSaved(false);
        setTestResult(null);
      }, 3000);
      
      console.log('Configuración API Workspace guardada en Firebase');
    } catch (error) {
      console.error('Error saving API Workspace config:', error);
      setTestResult({
        success: false,
        message: 'Error al guardar la configuración'
      });
      setTimeout(() => setTestResult(null), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTest = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    setTestResult(null);
    
    try {
      // Obtener las claves desencriptadas para el test
      const apiKeyToTest = editingKeys.apiKey ? config.googleApiKey : simpleDecrypt(config.googleApiKey);
      const clientIdToTest = editingKeys.clientId ? config.googleClientId : simpleDecrypt(config.googleClientId);
      
      console.log('Testing Google APIs...');
      
      // 🔥 TEST REAL - Verificar Google API Key con YouTube Data API (público, no requiere OAuth)
      if (apiKeyToTest) {
        console.log('Probando Google API Key con YouTube Data API...');
        
        const apiResponse = await fetch(
          `https://www.googleapis.com/youtube/v3/videos?part=snippet&chart=mostPopular&maxResults=1&regionCode=US&key=${apiKeyToTest}`,
          {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            }
          }
        );
        
        if (!apiResponse.ok) {
          if (apiResponse.status === 403) {
            const errorData = await apiResponse.json().catch(() => ({}));
            if (errorData.error?.message?.includes('YouTube Data API')) {
              throw new Error('API Key válida pero YouTube Data API no está habilitada en tu proyecto');
            } else {
              throw new Error('API Key inválida o sin permisos');
            }
          } else if (apiResponse.status === 400) {
            throw new Error('API Key mal formateada');
          } else if (apiResponse.status === 401) {
            throw new Error('API Key inválida o expirada');
          } else {
            throw new Error(`Error HTTP ${apiResponse.status}: ${apiResponse.statusText}`);
          }
        }
        
        const apiData = await apiResponse.json();
        console.log('✅ Google API Key válida - YouTube Data API responde correctamente');
        console.log('Respuesta de prueba:', apiData);
      }
      
      // 🔥 TEST ALTERNATIVO - Si YouTube falla, probar con Google Books API (también público)
      if (apiKeyToTest) {
        console.log('Probando Google API Key con Google Books API...');
        
        const booksResponse = await fetch(
          `https://www.googleapis.com/books/v1/volumes?q=javascript&maxResults=1&key=${apiKeyToTest}`,
          {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            }
          }
        );
        
        if (booksResponse.ok) {
          const booksData = await booksResponse.json();
          console.log('✅ Google API Key válida - Google Books API responde correctamente');
        }
      }
      
      // 🔥 TEST REAL - Verificar formato del Client ID
      if (clientIdToTest) {
        console.log('Probando Google Client ID...');
        
        // Verificar formato básico del Client ID
        const clientIdPattern = /^\d+-[a-zA-Z0-9_]+\.apps\.googleusercontent\.com$/;
        if (!clientIdPattern.test(clientIdToTest)) {
          throw new Error('Client ID no tiene el formato correcto de Google OAuth (debe terminar en .apps.googleusercontent.com)');
        }
        
        // Test adicional: Verificar que el Client ID existe usando Google's well-known config
        try {
          const wellKnownResponse = await fetch(
            'https://accounts.google.com/.well-known/openid_configuration',
            { method: 'GET' }
          );
          
          if (wellKnownResponse.ok) {
            console.log('✅ Google Client ID tiene formato válido y Google OAuth está accesible');
          }
        } catch (discError) {
          console.warn('No se pudo verificar completamente el Client ID:', discError.message);
          // No fallar el test por esto
        }
      }
      
      // ✅ TEST EXITOSO
      let successMessage = 'APIs de Google verificadas exitosamente';
      if (apiKeyToTest && clientIdToTest) {
        successMessage += ' (API Key + Client ID)';
      } else if (apiKeyToTest) {
        successMessage += ' (API Key funcional)';
      } else if (clientIdToTest) {
        successMessage += ' (Client ID con formato válido)';
      }
      
      setTestResult({
        success: true,
        message: successMessage
      });
      
      setTimeout(() => setTestResult(null), 5000);
      
    } catch (error) {
      console.error('Error testing Google APIs:', error);
      
      // Mensajes de error más específicos
      let errorMessage = 'Error al probar las APIs de Google';
      
      if (error.message.includes('API Key')) {
        errorMessage = `❌ ${error.message}`;
      } else if (error.message.includes('Client ID')) {
        errorMessage = `❌ ${error.message}`;
      } else if (error.message.includes('YouTube Data API')) {
        errorMessage = `⚠️ ${error.message}. Intenta habilitar YouTube Data API en Google Cloud Console`;
      } else if (error.message.includes('fetch')) {
        errorMessage = '❌ Error de conexión. Verifica tu conexión a internet';
      } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        errorMessage = '❌ Error de CORS o conexión. Verifica que la API Key sea correcta';
      } else {
        errorMessage = `❌ ${error.message}`;
      }
      
      setTestResult({
        success: false,
        message: errorMessage
      });
      
      setTimeout(() => setTestResult(null), 8000);
    } finally {
      setIsLoading(false);
    }
  };

  if (loadingConfig) {
    return (
      <div className={styles.loadingContainer}>
        <RefreshCw className={styles.loadingIcon} />
        <p>Cargando configuración de API Workspace...</p>
      </div>
    );
  }

  return (
    <div className={styles.apiContainer}>
      <div className={styles.apiContent}>
        {/* Google API Configuration */}
        <div className={styles.section}>
          <h3>
            <FaGoogle className={styles.sectionIcon} />
            Google Workspace APIs
          </h3>
          
          <div className={styles.formGroup}>
            <label htmlFor="googleApiKey">
              Google API Key *
              {savedKeys.apiKey && !editingKeys.apiKey && (
                <span className={styles.keyStatus}>
                  <FaCheck className={styles.savedIcon} />
                  Guardada y Encriptada
                </span>
              )}
            </label>
            <div className={styles.keyGroup}>
              <input
                id="googleApiKey"
                type={showKeys.apiKey ? 'text' : 'password'}
                value={displayKeys.apiKey}
                onChange={(e) => handleInputChange('googleApiKey', e.target.value)}
                placeholder={editingKeys.apiKey ? "Ingrese la Google API Key" : "••••••••"}
                className={errors.googleApiKey ? styles.inputError : ''}
                disabled={!editingKeys.apiKey && savedKeys.apiKey}
              />
              
              <div className={styles.keyButtons}>
                {(editingKeys.apiKey || !savedKeys.apiKey) && (
                  <button
                    type="button"
                    className={styles.keyToggle}
                    onClick={() => setShowKeys(prev => ({ ...prev, apiKey: !prev.apiKey }))}
                    title={showKeys.apiKey ? 'Ocultar clave' : 'Mostrar clave'}
                  >
                    {showKeys.apiKey ? <FaEyeSlash /> : <FaEye />}
                  </button>
                )}
                
                {savedKeys.apiKey && !editingKeys.apiKey && (
                  <button
                    type="button"
                    className={styles.editButton}
                    onClick={() => handleEditKey('apiKey')}
                    title="Editar clave"
                  >
                    <FaEdit />
                  </button>
                )}
                
                {editingKeys.apiKey && savedKeys.apiKey && (
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => handleCancelEdit('apiKey')}
                    title="Cancelar edición"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>
            {errors.googleApiKey && <span className={styles.errorText}>{errors.googleApiKey}</span>}
            
            {savedKeys.apiKey && !editingKeys.apiKey && (
              <small className={styles.keyHint}>
                <FaShieldAlt /> La clave está guardada de forma segura y encriptada. Haz clic en editar para cambiarla.
              </small>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="googleClientId">
              Google Client ID *
              {savedKeys.clientId && !editingKeys.clientId && (
                <span className={styles.keyStatus}>
                  <FaCheck className={styles.savedIcon} />
                  Guardado y Encriptado
                </span>
              )}
            </label>
            <div className={styles.keyGroup}>
              <input
                id="googleClientId"
                type={showKeys.clientId ? 'text' : 'password'}
                value={displayKeys.clientId}
                onChange={(e) => handleInputChange('googleClientId', e.target.value)}
                placeholder={editingKeys.clientId ? "Ingrese el Google Client ID" : "••••••••"}
                className={errors.googleClientId ? styles.inputError : ''}
                disabled={!editingKeys.clientId && savedKeys.clientId}
              />
              
              <div className={styles.keyButtons}>
                {(editingKeys.clientId || !savedKeys.clientId) && (
                  <button
                    type="button"
                    className={styles.keyToggle}
                    onClick={() => setShowKeys(prev => ({ ...prev, clientId: !prev.clientId }))}
                    title={showKeys.clientId ? 'Ocultar clave' : 'Mostrar clave'}
                  >
                    {showKeys.clientId ? <FaEyeSlash /> : <FaEye />}
                  </button>
                )}
                
                {savedKeys.clientId && !editingKeys.clientId && (
                  <button
                    type="button"
                    className={styles.editButton}
                    onClick={() => handleEditKey('clientId')}
                    title="Editar clave"
                  >
                    <FaEdit />
                  </button>
                )}
                
                {editingKeys.clientId && savedKeys.clientId && (
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => handleCancelEdit('clientId')}
                    title="Cancelar edición"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>
            {errors.googleClientId && <span className={styles.errorText}>{errors.googleClientId}</span>}
            
            {savedKeys.clientId && !editingKeys.clientId && (
              <small className={styles.keyHint}>
                <FaShieldAlt /> El Client ID está guardado de forma segura y encriptado. Haz clic en editar para cambiarlo.
              </small>
            )}
          </div>
        </div>

        {/* Información de seguridad */}
        <div className={styles.infoBox}>
          <FaInfoCircle className={styles.infoIcon} />
          <div>
            <h4>Información de Seguridad</h4>
            <ul>
              <li><strong>Encriptación:</strong> Todas las claves se almacenan encriptadas en Firebase</li>
              <li><strong>Acceso:</strong> Solo administradores pueden ver y editar estas claves</li>
              <li><strong>Google API Key:</strong> Obtén tu clave desde Google Cloud Console</li>
              <li><strong>Google Client ID:</strong> Configura OAuth 2.0 en tu proyecto de Google</li>
              <li><strong>Variables de entorno:</strong> REACT_APP_GOOGLE_API_KEY y REACT_APP_GOOGLE_CLIENT_ID</li>
            </ul>
          </div>
        </div>

        {/* Resultado del test */}
        {testResult && (
          <div className={`${styles.testResult} ${testResult.success ? styles.success : styles.error}`}>
            {testResult.success ? <FaCheck /> : <FaTimes />}
            <span>{testResult.message}</span>
          </div>
        )}

        {/* Botones de acción */}
        <div className={styles.actions}>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className={`${styles.button} ${styles.primary} ${isSaved ? styles.saved : ''}`}
          >
            {isLoading ? (
              <div className={styles.spinner}></div>
            ) : isSaved ? (
              <FaCheck />
            ) : (
              <FaSave />
            )}
            {isSaved ? 'Guardado en Firebase' : 'Guardar configuración'}
          </button>

          <button
            onClick={handleTest}
            disabled={isLoading}
            className={`${styles.button} ${styles.secondary}`}
          >
            {isLoading ? (
              <div className={styles.spinner}></div>
            ) : (
              <FaGoogle />
            )}
            Probar APIs
          </button>
        </div>
      </div>
    </div>
  );
};

export default APIWorkspace;